import { useRef, useState } from "react";
import { toast } from "sonner";
import { Paperclip, Send, X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useApp } from "@/context/AppContext";
import { api, errMsg } from "@/lib/api";

const ACCEPT = "image/*,application/pdf,.doc,.docx,.xls,.xlsx,.csv,.txt";

export function Composer({ onSend, placeholder = "Type your message...", testid = "composer" }) {
  const { settings } = useApp();
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const ref = useRef(null);
  const allowFiles = settings?.messaging?.attachments !== false;

  const upload = async (list) => {
    setUploading(true);
    for (const f of list) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post("/messages/files", fd);
        setFiles((x) => [...x, r.data]);
      } catch (e) { toast.error(errMsg(e), { id: "msg-upload-error" }); }
    }
    setUploading(false);
    if (ref.current) ref.current.value = "";
  };

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() && !files.length) { toast.error("Please type a message or attach a file", { id: "msg-empty" }); return; }
    setBusy(true);
    try {
      await onSend(text, files.map((f) => f.id));
      setText("");
      setFiles([]);
    } catch (err) { toast.error(errMsg(err), { id: "msg-send-error" }); } finally { setBusy(false); }
  };

  return (
    <form onSubmit={send} className="space-y-3 border-t border-slate-200 bg-slate-50 p-4" data-testid={testid}>
      <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder={placeholder} rows={3} className="bg-white text-base" data-testid={`${testid}-text`} />
      {files.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {files.map((f) => (
            <span key={f.id} className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white py-1 pl-3 pr-1 text-sm">
              {f.filename}
              <button type="button" onClick={() => setFiles(files.filter((x) => x.id !== f.id))} className="rounded-full p-1 hover:bg-slate-100" data-testid={`${testid}-remove-file`}><X className="h-3.5 w-3.5" /></button>
            </span>
          ))}
        </div>
      )}
      <div className="flex items-center justify-between gap-2">
        {allowFiles ? (
          <>
            <input ref={ref} type="file" multiple accept={ACCEPT} className="hidden" onChange={(e) => upload([...(e.target.files || [])])} data-testid={`${testid}-file-input`} />
            <Button type="button" variant="outline" onClick={() => ref.current?.click()} disabled={uploading || files.length >= 5} data-testid={`${testid}-attach`}>
              {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Paperclip className="mr-2 h-4 w-4" />} Attach file or photo
            </Button>
          </>
        ) : <span />}
        <Button type="submit" disabled={busy || uploading} data-testid={`${testid}-send`}>
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />} Send
        </Button>
      </div>
    </form>
  );
}
